/**
 * Shared scroll-reveal for AGNT pages.
 *
 * Any element matching `selector` inside the ref'd container fades-slides in
 * (via the global `is-revealed` class) when it enters the viewport. The reveal
 * CSS itself lives in `_reveal.scss` (`agnt-reveal-contract` mixin), included by
 * each page's wrapper module; `prefers-reduced-motion` is honoured there.
 *
 * Used by both `StyledShell` (the standard AGNT page chrome) and the home page,
 * which composes its own hero and so can't reuse the shell wholesale.
 */

import { useEffect, type RefObject } from 'react';

const DEFAULT_SELECTOR = '[data-reveal]';

interface ScrollRevealOptions {
  /** Elements to reveal. Defaults to the shared `[data-reveal]` opt-in hook. */
  selector?: string;
  /** Re-run the observer when these change (e.g. async body content). */
  deps?: unknown[];
  /**
   * IntersectionObserver threshold. Defaults to 0.1 (10% visible). Use 0 for
   * pages with very tall sections (e.g. legal pages) where 10% may never fit
   * in the mobile viewport, preventing the reveal from ever firing.
   */
  threshold?: number;
}

export default function useScrollReveal(
  rootRef: RefObject<HTMLElement>,
  { selector = DEFAULT_SELECTOR, deps = [], threshold = 0.1 }: ScrollRevealOptions = {}
) {
  useEffect(() => {
    const container = rootRef.current;
    if (!container) return;

    const elements = container.querySelectorAll(selector);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -60px 0px' }
    );

    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootRef, selector, ...deps]);
}
